import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Table, InputGroup, Row, Col, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import Customer2SaleModal from '../components/Customer2SaleModal';
import ConfirmModal from '../components/ConfirmModal';
import * as XLSX from 'xlsx';

const Kreditlar = () => {
  const { token, user } = useAuth();

  const getAuthHeaders = (includeJson = false) => {
    const baseHeaders = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(user?.ownerId || user?.id ? { 'X-Owner-Id': String(user?.ownerId || user?.id) } : {}),
      ...(user?.staffId ? { 'X-Staff-Id': String(user?.staffId) } : {}),
    };
    return includeJson ? { 'Content-Type': 'application/json', ...baseHeaders } : baseHeaders;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const [q, setQ] = useState('');
  const [mode, setMode] = useState('missing_days'); // missing_days | missing_payment | completed | near | all
  const [nearStart, setNearStart] = useState(''); // YYYY-MM-DD
  const [nearEnd, setNearEnd] = useState(''); // YYYY-MM-DD

  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (mode === 'missing_days') {
      params.set('status', 'false');
      params.set('sort', 'missing_days');
    } else if (mode === 'missing_payment') {
      params.set('status', 'false');
      params.set('sort', 'missing_payment');
    } else if (mode === 'completed') {
      params.set('status', 'true');
    } else if (mode === 'near') {
      if (nearStart && nearEnd) {
        params.set('start', nearStart);
        params.set('end', nearEnd);
      }
    }
    return params.toString();
  }, [q, mode, nearStart, nearEnd]);

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError('');
      const qs = queryParams ? `?${queryParams}` : '';
      const res = await fetch(`/api/credit${qs}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to fetch credits');
      }
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch near mode when both dates provided
    if (mode === 'near' && (!nearStart || !nearEnd)) {
      setRows([]);
      return;
    }
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleOpenView = (customer) => {
    setSelectedCustomer(customer);
    setShowSaleModal(true);
  };

  const handleConfirmComplete = (customer) => {
    setConfirmTarget(customer);
    setConfirmOpen(true);
  };

  const markCompleted = async () => {
    if (!confirmTarget) return;
    try {
      const res = await fetch(`/api/credit/${confirmTarget.id}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(true),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to mark completed');
      }
      setConfirmOpen(false);
      setConfirmTarget(null);
      fetchRows();
    } catch (err) {
      setError(err.message || 'Error updating status');
    }
  };

  const handleExportExcel = () => {
    // Prepare data for Excel
    const excelData = [
      ['Name', 'Phone', 'Missing Payment', 'Missing Days', 'Total Debt', 'Status'],
      ...(rows || []).map(r => [
        r.name || '',
        r.phone || '',
        Number(r.missing_payment || 0),
        Number(r.missing_days || 0),
        Number(r.overallDebt || 0),
        r.status ? 'Completed' : 'Pending',
      ])
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kreditlar');

    // Generate file name with current date and time
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const fileName = `Kreditlar_${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}-${pad(now.getMinutes())}.xlsx`;

    // Save the file
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="p-3">
      <h5 className="mb-3">Kreditlar</h5>

      {/* Filters Row */}
      <div className="mb-3">
        <Row className="g-2 align-items-end">
          <Col sm={12} md={5} lg={4}>
            <InputGroup>
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                placeholder="name or phone"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs="auto">
            <Button
              variant={mode === 'missing_days' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('missing_days')}
            >
              Missing days
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant={mode === 'missing_payment' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('missing_payment')}
            >
              Missing payments
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant={mode === 'completed' ? 'success' : 'outline-success'}
              onClick={() => setMode('completed')}
            >
              Completed
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant={'outline-secondary'}
              onClick={() => {
                setQ('');
                setNearStart('');
                setNearEnd('');
                setMode('all');
              }}
            >
              Clear
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant={'outline-success'}
              onClick={handleExportExcel}
            >
              Excel
            </Button>
          </Col>
        </Row>
        <Row className="g-2 align-items-end mt-2">
          <Col xs="auto">
            <div className="fw-bold">Near payment</div>
          </Col>
          <Col xs="auto">
            <Form.Label className="me-2">From</Form.Label>
            <Form.Control
              type="date"
              value={nearStart}
              onChange={(e) => {
                setNearStart(e.target.value);
                setMode('near');
              }}
              style={{ minWidth: 180 }}
            />
          </Col>
          <Col xs="auto">
            <Form.Label className="me-2">To</Form.Label>
            <Form.Control
              type="date"
              value={nearEnd}
              onChange={(e) => {
                setNearEnd(e.target.value);
                setMode('near');
              }}
              style={{ minWidth: 180 }}
            />
          </Col>
        </Row>
      </div>

      {/* Status / Error */}
      {loading && (
        <div className="d-flex align-items-center mb-2">
          <div className="spinner-border me-2" role="status" /> Loading...
        </div>
      )}
      {error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}

      {/* Table */}
      <div className='table-responsive shadow border border-4  '>
      <Table hover size="sm" className="bg-white ">
        <thead>
          <tr>
            <th>Name</th>
            <th>Phone</th>
            <th>Missing payment</th>
            <th>Missing days</th>
            <th>Total debt</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center">No data</td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <span className="fw-semibold">{r.name || '-'}</span>
                </td>
                <td>{r.phone || '-'}</td>
                <td className={Number(r.missing_payment || 0) > 0 ? 'text-danger fw-semibold' : ''}>{Number(r.missing_payment || 0)}</td>
                <td className={Number(r.missing_days || 0) > 0 ? 'text-danger fw-semibold' : ''}>{Number(r.missing_days || 0)}</td>
                <td>{Number(r.overallDebt || 0)}</td>
                <td>
                  {r.status ? <Badge bg="success">Completed</Badge> : <Badge bg="warning" text="dark">Pending</Badge>}
                </td>
                <td>
                  <div className="d-flex" style={{ gap: 8 }}>
                    <Button
                      size="sm"
                      variant="info"
                      onClick={() => handleOpenView(r)}
                    >
                      View
                    </Button>
                    {!r.status && (
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleConfirmComplete(r)}
                      >
                        Mark completed
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      </div>
      {/* View Sale Modal (read-only via existing customer selection) */}
      {showSaleModal && (
        <Customer2SaleModal
          show={showSaleModal}
          onHide={() => setShowSaleModal(false)}
          cart={[]}
          customer2={selectedCustomer}
          onCreateCustomer2={() => {}}
          deliveryPrice={0}
        />
      )}

      {/* Confirmation Modal to mark completed */}
      <ConfirmModal
        show={confirmOpen}
        onCancel={() => { setConfirmOpen(false); setConfirmTarget(null); }}
        onConfirm={markCompleted}
        message={confirmTarget ? `Mark ${confirmTarget.name} as completed?` : ''}
      />
    </div>
  );
};

export default Kreditlar;